<template>
  <div
    class="userAvatar relative flex shrink-0 cursor-pointer select-none transition-[filter] duration-150 hover:brightness-110"
    :class="[
      !isUser && 'workspace',
      placement === 'appHeader' ? 'w-[1.5625rem] h-[1.5625rem] mt-0.5' : size === 'sm' ? 'size-7' : 'w-8 h-8',
      selected && 'z-1',
    ]"
    @click="emit('click', user)">

    <img
      v-if="botLogo"
      class="in"
      :class="ringClass"
      :src="botLogo"
      :title="user.username"
    />
    <div
      v-else-if='!user || !user.avatarFileName'
      class="in letterAvatar w-full h-full overflow-hidden flex flex-col items-center justify-center text-center text-white"
      :class="[inShapeClass, ringClass]"
      :title='!user ? $t("search.unassigned") : (user.username || user.name)'>
      <div
        class='content w-full h-full flex flex-col items-center justify-center text-center text-white'
        :class="size === 'sm' ? 'text-[0.75rem]' : 'text-[1rem]'"
        :style="letterAvatar.style"
      >
        <i v-if="letterAvatar.questionMark" :class="[QUESTION_MARK_CLASSES, size === 'sm' ? 'text-label' : 'text-body']"></i>
        <template v-else>{{ letterAvatar.initials }}</template>
      </div>
    </div>
    <img
      v-else
      class="in w-full overflow-hidden bg-white"
      :class="[inShapeClass, ringClass]"
      crossorigin="anonymous"
      :src='baseUrl'
      :title='(user && user.username) || user.name'
    />
    <div
      v-if="
        !noPendingBadge &&
        user &&
        user.email &&
        (user.userWorkspace || user.userWorkspaces) &&
        !(
          (user.userWorkspace && user.userWorkspace.registrationCompleted) ||
          (user.userWorkspaces && user.userWorkspaces.registrationCompleted)
        )
      "
      class='absolute -bottom-[0.1875rem] -right-[0.1875rem] w-4 h-4 flex items-center justify-center rounded-full text-[0.875rem] leading-none text-accent drop-shadow-[-1px_-1px_4px_black]'
      :title='$t("search.sign_up_not_confirmed")'>
      <PendingIcon />
    </div>
    <i v-if="withStar && user"
      :title="$t('ui.user_avatar_executor_title', { username: user.username })"
      class="tr tr-star absolute -bottom-2 -right-1.5 text-[1rem] text-accent text-shadow-[0_0_4px_black]"
    ></i>
    <!-- bottom-right, next to the executor star -->
    <span
      v-if="onDayoff"
      class="absolute -bottom-[0.1875rem] -right-[0.1875rem] w-4 h-4 flex items-center justify-center rounded-full text-[0.875rem] leading-none text-accent drop-shadow-[-1px_-1px_4px_black]"
      :title="$t('dayoff.on_dayoff_today')"
    ><VactionIcon /></span>
    <!-- top-right so it never collides with the day off badge / executor star at the bottom -->
    <span
      v-if="mcpAgent"
      class="absolute -top-1 -right-1.5 w-[0.9375rem] h-[0.9375rem] flex items-center justify-center rounded-full text-body leading-none text-accent bg-bg"
      :title="$t('common.Executed_by_agent', { agent: mcpAgent.label })"
    >
      <img v-if="mcpAgent.icon" :src="mcpAgent.icon" class="w-[0.6875rem] h-[0.6875rem] bg-transparent" alt="" />
      <AgentIcon v-else />
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import {
  IconTravelRounded as VactionIcon,
  IconSmartToyRounded as AgentIcon,
  IconScheduleRounded as PendingIcon,
} from '@iconify-prerendered/vue-material-symbols';
import describeMcpAgent from '@/utils/mcpClient';
import { imgproxyUrl } from '@/utils/imgproxy';

const QUESTION_MARK_CLASSES = 'tr tr-question-mark w-full h-full flex items-center justify-center text-text-muted';

const AVATAR_COLOR = '#7f8c8d';

const props = defineProps({
  fromMcp: [String, null],
  user: [Object, null],
  noUserText: [String],
  pendingStatus: [String, null],
  placement: [String, null],
  size: String,
  isUser: {
    type: Boolean,
    default: true,
  },
  noBorder: {
    type: Boolean,
    default: false,
  },
  noPendingBadge: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  withStar: {
    type: Boolean,
    default: false,
  },
  withDayoffBadge: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

const store = useStore();

// workspaces are squarish cards, users are circles
const inShapeClass = computed(() => (props.isUser
  ? 'rounded-full'
  : 'rounded-[0.25rem] shadow-[1px_1px_5px_var(--shadow-color)]'));

const ringClass = computed(() => {
  const active = props.active && !props.noBorder;
  if (active && props.selected) {
    return 'shadow-[0_0_0_1px_var(--bg),0_0_0_2px_var(--text-muted),0_0_3px_5px_var(--accent)]!';
  }
  if (active) {
    return 'shadow-[0_0_3px_var(--accent),0_0_3px_3.3px_var(--accent)]!';
  }
  if (props.selected) {
    return 'shadow-[0_0_0_1px_var(--bg),0_0_0_2px_var(--text-muted)]!';
  }
  return '';
});

// A bot without an avatar of its own wears its integration's logo.
const botLogo = computed(() => {
  const integration = props.user && props.user.botClassId && !props.user.avatarFileName
    && store.state.project.integrations?.[props.user.botClassId];
  return integration ? integration.logo : null;
});

const mcpAgent = computed(() => describeMcpAgent(props.fromMcp));

const onDayoff = computed(() => !!(props.withDayoffBadge && props.user && props.isUser
  && store.getters['dayoff/isOnDayoffToday'](props.user.id)));

const baseUrl = computed(() => {
  if (props.user.avatarFileName.match(/(.png|.jpeg|.jpg)/gm)) {
    return props.user.avatarFileName;
  }
  return imgproxyUrl(`${window.s3base}/${props.isUser ? 'useravatars' : 'workspaceavatars'}/${props.user.id}/`
    + `${props.user.avatarFileName}.png`, 'avatar');
});

// Rendered by the template, not written into the DOM after mount: a task card has avatars, and a style write after
// mount between two cards' layout reads made the browser recalculate the board's layout once per card.
const letterAvatar = computed(() => {
  const name = props.user && (props.user.username || props.user.name);
  if (!name) {
    return props.isUser
      ? { questionMark: true, style: { backgroundColor: 'var(--bg2)' } }
      : { style: {} };
  }
  const [first, second] = name.split(' ');
  const initials = first.charAt(0).toUpperCase() + ((second && second.charAt(0).toUpperCase()) || '');
  return props.user.username
    ? { initials, style: { backgroundColor: AVATAR_COLOR } }
    : { initials, style: { backgroundColor: 'white', color: AVATAR_COLOR, fontWeight: 'bold' } };
});
</script>
